/**
 * A type that represents the returns of Object.entries.
 * This preserves the type of the values in the object.
 *
 * Object.entries doesn't guarantee the keys in an object are only of that type.
 * That must be checked before accessing the entries safely.
 *
 * Without this type, the return type of Object.entries would be [string, T][], where T would be Any.
 * With this type, the return type of Object.entries will return a strongly typed object.
 */
export type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][] &
  [string, T][];

export type Keys<T> = Array<keyof T>;

export type Values<T> = Array<T[keyof T]>;

/**
 * A strongly typed version of `key in obj` that returns a boolean.
 *
 * This is useful for checking if a key is in an object and preserving the type of the key.
 * `key in obj` will require a follow up cast to use the key as a `keyof T`, where as this
 * function will preserve the type and allow for immediate use without any casting
 *
 * @param key - The key to check for in the object.
 * @param obj - The object to check the key against.
 * @returns Boolean representing whether the key is in the object.
 */
export function isObjKey<T extends object>(key: PropertyKey, obj: T): key is keyof T {
  return key in obj;
}
